import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RecycleEntity, RecycleJob, RecycleList, RecycleRow } from '@klothiq/domain';
import { recyclePreviewSchema } from '@klothiq/validation';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import { recycleErrorMessage, requireRecycleSuccess } from '@/lib/recycleErrors';

async function recycleRpc<T>(name: string, params: object): Promise<T> {
  const { data, error } = await supabase.rpc(name, { p_data: params });
  if (error) throw new Error(recycleErrorMessage(error));
  if (!data) throw new Error('No response from the recycle bin. Please refresh.');
  return data as T;
}

export function useRecycleBin(entity: RecycleEntity, search: string, page: number) {
  const { organization, role, profile } = useAuth();
  return useQuery({
    queryKey: ['recycle_bin', organization?.id, entity, search, page],
    enabled: !!organization && !!profile?.is_active && (role === 'owner' || role === 'admin'),
    queryFn: () =>
      recycleRpc<RecycleList>('list_recycle_bin', { entity, search, offset: page * 25, limit: 25 }),
    staleTime: 15_000,
  });
}

export function useRecyclePreview() {
  return useMutation({
    mutationFn: (params: {
      entity: RecycleEntity;
      scope: 'all' | 'selected';
      search: string;
      ids?: string[];
      request_key: string;
    }) => recycleRpc<RecycleJob>('preview_recycle_bin_purge', recyclePreviewSchema.parse(params)),
  });
}

export function useRecycleBatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (params: { job_id: string; reason: string; confirmation: string }) =>
      recycleRpc<RecycleJob>('purge_recycle_bin_batch', params),
    onSuccess: () => refreshSaleConsumers(client),
  });
}

export function useRecycleJob(jobId: string | null, offset = 0) {
  const { organization, profile, role } = useAuth();
  return useQuery({
    queryKey: ['recycle_job', organization?.id, profile?.id, jobId, offset],
    enabled: !!jobId && !!profile?.is_active && (role === 'owner' || role === 'admin'),
    queryFn: () => recycleRpc<RecycleJob>('get_recycle_bin_purge_job', { job_id: jobId, offset }),
    staleTime: 0,
  });
}

export function useCancelRecycleJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (job_id: string) =>
      recycleRpc<RecycleJob>('cancel_recycle_bin_purge_job', { job_id }),
    onSuccess: () => refreshSaleConsumers(client),
  });
}

export function usePendingRecycleJob(enabled: boolean) {
  const { organization, profile, role } = useAuth();
  return useQuery({
    queryKey: ['recycle_job', organization?.id, profile?.id, 'pending'],
    enabled: enabled && !!profile?.is_active && (role === 'owner' || role === 'admin'),
    queryFn: async (): Promise<RecycleJob | null> => {
      const { data, error } = await supabase.rpc('get_recycle_bin_purge_job', { p_data: {} });
      if (error) throw new Error(recycleErrorMessage(error));
      return data as RecycleJob | null;
    },
    staleTime: 0,
  });
}

export function useRecycleRestore() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entity,
      row,
      activateVariants,
    }: {
      entity: RecycleEntity;
      row: RecycleRow;
      activateVariants?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('restore_recycle_bin_item', {
        p_data: {
          entity,
          id: row.id,
          expected_version: row.version,
          activate_variants: activateVariants ?? false,
        },
      });
      requireRecycleSuccess(data, error);
      return data;
    },
    onSuccess: () => refreshSaleConsumers(client),
  });
}
